import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  fetchNotificationPreferences,
  putNotificationPreferenceGroup,
  type NotificationPreferences,
} from "@/api/notifications";

const PREFS_KEY = ["notificationPreferences"] as const;

export function useNotificationPreferences() {
  return useQuery({
    queryKey: PREFS_KEY,
    queryFn: ({ signal }) => fetchNotificationPreferences(signal),
    staleTime: 60_000,
  });
}

/** Optimistic group toggle; PUT returns the fresh grouped model. */
export function usePutNotificationPreferenceGroup() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: putNotificationPreferenceGroup,
    onMutate: async ({ groupKey, channel, value }) => {
      await qc.cancelQueries({ queryKey: PREFS_KEY });
      const prev = qc.getQueryData<NotificationPreferences>(PREFS_KEY);

      if (prev) {
        qc.setQueryData<NotificationPreferences>(PREFS_KEY, {
          ...prev,
          configurable: prev.configurable.map((g) =>
            g.groupKey === groupKey
              ? { ...g, [channel === "email" ? "email" : "inApp"]: value }
              : g,
          ),
        });
      }

      return prev;
    },
    onError: (_e, _v, prev) => prev && qc.setQueryData(PREFS_KEY, prev),
    onSuccess: (fresh) => qc.setQueryData(PREFS_KEY, fresh),
  });
}
